import React, { useState } from 'react';
import { Mail, MapPin, Clock, Send } from 'lucide-react';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contactInfo = [
    {
      icon: <Mail className="w-5 h-5" />,
      title: "Email",
      value: "info@prestigious-marketing.com",
      link: "mailto:info@prestigious-marketing.com"
    },
    {
      icon: <MapPin className="w-5 h-5" />,
      title: "Location",
      value: "Orlando, Florida",
      link: null
    },
    {
      icon: <Clock className="w-5 h-5" />,
      title: "Availability",
      value: "We're always available!",
      link: null
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        toast.success('Message sent! We\'ll get back to you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="container-max mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
            Get In Touch
          </span>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 mb-6 max-w-3xl mx-auto">
            Let's Start a Conversation
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Have a question or ready to transform your marketing? We're here to help. 
            Reach out and let's discuss how we can grow your business together.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white" data-testid="contact-section">
        <div className="container-max mx-auto">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Contact Information</h2>
              <div className="space-y-6 mb-8">
                {contactInfo.map((info, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#1E3A8A] text-white rounded-xl flex items-center justify-center flex-shrink-0">
                      {info.icon}
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900">{info.title}</h3>
                      {info.link ? (
                        <a href={info.link} className="text-slate-600 hover:text-[#1E3A8A] transition-colors">
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-slate-600">{info.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <Card className="bg-[#0F172A] border-0">
                <CardContent className="p-6 text-white">
                  <h3 className="font-bold mb-2">Partnership Inquiries</h3>
                  <p className="text-slate-400 text-sm mb-4">
                    Interested in becoming a partner? Contact our partnership team directly:
                  </p>
                  <a 
                    href="mailto:jeremy.c@prestigioustravelclub.org"
                    className="text-[#EA580C] font-medium hover:underline text-sm"
                  >
                    jeremy.c@prestigioustravelclub.org
                  </a>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-slate-200">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6">Send Us a Message</h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name" className="text-slate-700 font-medium">
                          Full Name *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Smith"
                          className="mt-1.5"
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-slate-700 font-medium">
                          Email *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                          className="mt-1.5"
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone" className="text-slate-700 font-medium">
                          Phone
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(555) 123-4567"
                          className="mt-1.5"
                          data-testid="contact-phone-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="company" className="text-slate-700 font-medium">
                          Company
                        </Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Your Company"
                          className="mt-1.5"
                          data-testid="contact-company-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-slate-700 font-medium">
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="How can we help you?"
                        className="mt-1.5 min-h-[150px]"
                        data-testid="contact-message-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full sm:w-auto bg-[#1E3A8A] hover:bg-[#1e40af] text-white px-8"
                      disabled={isSubmitting}
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                      <Send className="ml-2 w-4 h-4" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-max mx-auto text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6">
              We Guarantee We Won't Be Outworked
            </h2>
            <p className="text-slate-600 text-lg mb-4">
              Our time may say "By Appointment" but that's only a suggestion.
            </p>
            <p className="text-slate-600">
              We are your strategic partner who will not stop until we help you dominate your space. 
              We only win together!
            </p>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="cta-gradient py-20">
        <div className="container-max mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <blockquote className="max-w-3xl mx-auto">
            <p className="text-2xl sm:text-3xl font-light text-white italic mb-6">
              "The aim of marketing is to know and understand the customer so well 
              the product or service fits him and sells itself."
            </p>
            <cite className="text-slate-400 not-italic font-medium">— Peter Drucker</cite>
          </blockquote>
        </div>
      </section>
    </div>
  );
};

export default Contact;
